import { EventEmitter } from 'fbemitter'
import { remove, uniqWith, isEqual, isNumber, debounce } from 'lodash'
import { valueBasedOnMinMaxIsValid } from '../views/campaigns/helpers/min_max_validator'

class AmountValidator {
  private amount: number

  private minAmount: string | number

  private maxAmount: string | number

  private amountStep: number

  private tagsAmount: any

  private errorsCounter: number

  constructor(amount, options) {
    this.amount = parseInt(amount, 10) || 0
    this.minAmount = parseInt(options.minAmount, 10) || ''
    this.maxAmount = parseInt(options.maxAmount, 10) || ''
    this.amountStep = parseInt(options.amountStep, 10) || 1
    this.tagsAmount = options.tagsAmount || []
    this.errorsCounter = 0
  }

  isSuccess() {
    return !this.errorsCounter
  }

  validatedMinMax() {
    const result = valueBasedOnMinMaxIsValid(this.amount, this.minAmount, this.maxAmount)

    if (result) {
      return true
    }

    this.errorsCounter += 1
    return false
  }

  validateTagsAmountsDuplicates() {
    const result = this.tagsAmount.length < 2

    if (result) {
      return true
    }

    this.errorsCounter += 1
    return false
  }

  validateTagsAmountsConflicts() {
    const result = isNumber(this.amount) && (this.tagsAmount.length == 0 || this.tagsAmount.includes(this.amount))

    if (result) {
      return true
    }

    this.errorsCounter += 1
    return false
  }

  validateAmountStep() {
    if (this.amountStep <= 1) {
      return true
    }

    const result = this.amount % this.amountStep === 0

    if (result) {
      return true
    }

    this.errorsCounter += 1
    return false
  }
}

class CampaignAmountsStore extends EventEmitter {
  static CONFIG = {
    errorTypes: {
      minMax: 'MinMax',
      tagsAmountsDuplicates: 'TagsAmountsDuplicates',
      tagsAmountsConflicts: 'TagsAmountsConflicts',
      amountStep: 'AmountStep',
    },
  }

  private amounts: { [elementId: string]: { [locationId: string]: number } }

  private campaignElementIds: { [key: string]: { campaignElementId: number | null; itemId: number | null } }

  private errors: { minMax: any[]; tagsAmountsDuplicates: any[]; tagsAmountsConflicts: any[]; amountStep: any[] }

  private queueProcessedElementIds: any[]

  constructor() {
    super()
    this.amounts = {}
    this.campaignElementIds = {}
    this.errors = { minMax: [], tagsAmountsDuplicates: [], tagsAmountsConflicts: [], amountStep: [] }
    this.queueProcessedElementIds = []
  }

  get config() {
    return CampaignAmountsStore.CONFIG
  }

  setAmount(elementId, locationId, amount, validateOptions = null, campaignElementId = null, itemId = null) {
    this.queueProcessedElementIds.push(elementId)
    this.amounts[elementId] = Object.assign({}, this.amounts[elementId] || {}, { [locationId]: parseInt(amount) })
    if (validateOptions) {
      this._validate(amount, { elementId, locationId }, validateOptions)
    }
    this.campaignElementIds[[elementId, locationId].join('-')] = { campaignElementId, itemId }
    this.emit('AmountWasChanged', elementId, locationId, amount)
    debounce(() => this._performFinalActions(), 1)()
  }

  setNewAmountThroughEvent(elementId, value) {
    Object.keys(this.amounts[elementId] || {}).forEach((locationId) => {
      this.emit(`UpdateAmount-${elementId}-${locationId}`, value)
      this.emit('AmountWasChanged', elementId, locationId, value)
    })
  }

  getAmount(elementId, locationId) {
    const elementsAmounts = this.amounts[elementId] || {}
    return elementsAmounts[locationId] || 0
  }

  getIds(elementId, locationId) {
    return this.campaignElementIds[[elementId, locationId].join('-')]
  }

  getAmounts() {
    return this.amounts
  }

  totalFor(elementId) {
    const elementAmounts = this.amounts[elementId] || {}

    return Object.values(elementAmounts).reduce((result: number, val: number) => result + val, 0)
  }

  totalPrintsFor(elementId, pdfPagesCount) {
    const elementAmounts = this.amounts[elementId] || {}

    return Object.values(elementAmounts).reduce((result: number, val: number) => result + val, 0) * pdfPagesCount
  }

  _validate(amount, data, options) {
    const validator = new AmountValidator(amount, options)

    const errors = []

    if (validator.validatedMinMax()) {
      this._removeError('minMax', data)
    } else {
      this.errors.minMax.push(data)
      this.errors.minMax = uniqWith(this.errors.minMax, isEqual)
      errors.push(this.config.errorTypes.minMax)
    }

    if (validator.validateTagsAmountsDuplicates()) {
      this._removeError('tagsAmountsDuplicates', data)
    } else {
      this.errors.tagsAmountsDuplicates.push(data)
      this.errors.tagsAmountsDuplicates = uniqWith(this.errors.tagsAmountsDuplicates, isEqual)
      errors.push(this.config.errorTypes.tagsAmountsDuplicates)
    }

    if (validator.validateTagsAmountsConflicts()) {
      this._removeError('tagsAmountsConflicts', data)
    } else {
      this.errors.tagsAmountsConflicts.push(data)
      this.errors.tagsAmountsConflicts = uniqWith(this.errors.tagsAmountsConflicts, isEqual)
      errors.push(this.config.errorTypes.tagsAmountsConflicts)
    }

    if (validator.validateAmountStep()) {
      this._removeError('amountStep', data)
    } else {
      this.errors.amountStep.push(data)
      this.errors.amountStep = uniqWith(this.errors.amountStep, isEqual)
      errors.push(this.config.errorTypes.amountStep)
    }

    this.emit(`ValidationResult-${data.elementId}-${data.locationId}`, errors)
  }

  _removeError(type, { elementId, locationId }) {
    remove(this.errors[type], (err) => err.elementId == elementId && err.locationId == locationId)
  }

  _performFinalActions() {
    this._updateTotal()
    this._showErrors()
  }

  _updateTotal() {
    while (this.queueProcessedElementIds.length) {
      this.emit(`TotalWasChanged-${this.queueProcessedElementIds.shift()}`)
    }
  }

  _showErrors() {
    const { minMax, tagsAmountsDuplicates, tagsAmountsConflicts, amountStep } = this.errors

    this.emit('UpdateViewAmountMessagesArea', this.errors)
  }
}

export default new CampaignAmountsStore()
